import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import appApi from '../../../../api/app-api';
import { PendingWorkingHourFlat } from '../../../../api/types/accountants/salaries-pending-payments-flat/pending-working-hour-flat.type';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';

const toYyyyMmDd = (date: Date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** dd/mm/yyyy from UTC fields only — plain text, no locale/TZ shift (matches ISO calendar day). */
const formatIsoDateUtcPlainText = (value: Date | string): string => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

/** Same as salary-generator-flat UI — local he-IL time for התחלה / סיום. */
const formatTime = (date: Date) =>
  new Intl.DateTimeFormat('he-IL', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));

const parseTimeToExcelSerial = (value: string): number | null => {
  // Expect: HH:MM (as produced by Intl for time)
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (
    !Number.isFinite(hh) ||
    !Number.isFinite(mm) ||
    hh < 0 ||
    hh > 23 ||
    mm < 0 ||
    mm > 59
  ) {
    return null;
  }
  return (hh * 60 + mm) / 1440;
};

const calcHourUnitsNumber = (
  startAt: Date,
  endAt: Date,
  displayType: string,
): number => {
  const startMs = new Date(startAt).getTime();
  const endMs = new Date(endAt).getTime();
  const diffMinutes = Math.max(0, Math.floor((endMs - startMs) / 60000)); // no seconds
  const unitMinutes = displayType === 'academic_hour' ? 45 : 60;
  return diffMinutes / unitMinutes;
};

const getLocalDateKey = (date: Date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const exportPayrollReportFlatToExcelOperation = async (
  userIds: number[],
  branchIds: number[],
  startDate: Date,
  endDate: Date,
  includeRetroactivePayments: boolean,
  setLoading: (loading: boolean) => void,
) => {
  try {
    if (!startDate || !endDate) {
      toast.warn('יש לבחור תאריך התחלה וסיום');
      return;
    }
    if (userIds.length === 0) {
      toast.warn('יש לבחור לפחות מרצה אחד');
      return;
    }
    if (branchIds.length === 0) {
      toast.warn('יש לבחור לפחות שלוחה אחת');
      return;
    }

    setLoading(true);

    const limit = 1000;
    let offset = 0;
    let total = 0;
    const allRows: PendingWorkingHourFlat[] = [];

    do {
      const res = await appApi.generatePayrollReportFlat(
        userIds,
        branchIds,
        toYyyyMmDd(startDate),
        toYyyyMmDd(endDate),
        includeRetroactivePayments,
        offset,
        limit,
      );

      const page = res.data.body;
      total = page.total ?? 0;
      allRows.push(...(page.data ?? []));
      offset += limit;
    } while (allRows.length < total);

    // Match UI travel counting logic:
    // for each (user, date, branch) choose ONE row to "own" the travel payment
    // (highest travelRate wins; if tie, lowest workingHourId).
    type TravelOwner = { workingHourId: number; travelRate: number };
    const bestTravelOwnerByUserDateBranch = new Map<string, TravelOwner>();

    allRows.forEach((row) => {
      if (!row.addTravel) return;
      if (row.travelRate === null || row.travelRate === undefined) return;

      const travelRate = Number(row.travelRate);
      if (!Number.isFinite(travelRate) || travelRate <= 0) return;

      const key = `${row.userId}-${getLocalDateKey(row.startAt)}-${row.branchId}`;
      const existing = bestTravelOwnerByUserDateBranch.get(key);

      if (!existing) {
        bestTravelOwnerByUserDateBranch.set(key, {
          workingHourId: row.workingHourId,
          travelRate,
        });
        return;
      }

      if (
        travelRate > existing.travelRate ||
        (travelRate === existing.travelRate &&
          row.workingHourId < existing.workingHourId)
      ) {
        bestTravelOwnerByUserDateBranch.set(key, {
          workingHourId: row.workingHourId,
          travelRate,
        });
      }
    });

    const headers = [
      'שלוחה',
      /*'שם מרצה',*/

      'שם משפחה',
      'שם פרטי',
      'ת"ז',
      'אימייל',
      /*'טלפון',*/
      'קורס',
      'שם קורס נוסף',
      'סוג פעילות',
      'הערות שעות עבודה',
      'סוג שעה',
      'תאריך',
      'התחלה',
      'סיום',
      'הפסקות',

      'שעות (אקדמי/מלאות)',
      'תעריף',
      'תעריף נסיעות',
      'סה״כ',
      'שם הרצאה',
    ];

    const dataRows = allRows.map((row) => [
      row.branchName ?? '',
      /*`${String(row.lastName ?? '').trim()} ${String(row.firstName ?? '').trim()}`.trim(),*/
      row.lastName ?? '',
      row.firstName ?? '',
      row.nationalId ?? '',
      row.email ?? '',
      /*row.phoneNumber
        ? phoneNumberUtils.internationalToIsraeli(row.phoneNumber)
        : '',*/
      row.courseName ?? '',
      row.additionalCourseName ?? '',
      row.activityType ?? '',
      row.workingHourNotes ?? '',
      String(row.displayType ?? ''),
      row.startAt ? formatIsoDateUtcPlainText(row.startAt) : '',
      row.startAt ? formatTime(row.startAt) : '',
      row.endAt ? formatTime(row.endAt) : '',
      row.breakMinutes !== null && row.breakMinutes !== undefined
        ? row.breakMinutes
        : '',
      row.startAt && row.endAt
        ? calcHourUnitsNumber(
            row.startAt,
            row.endAt,
            String(row.displayType ?? ''),
          )
        : '',
      row.rate ?? '',
      (() => {
        const key = `${row.userId}-${getLocalDateKey(row.startAt)}-${row.branchId}`;
        const owner = bestTravelOwnerByUserDateBranch.get(key);
        return owner && owner.workingHourId === row.workingHourId
          ? owner.travelRate
          : 0;
      })(),
      (() => {
        const rate = Number(row.rate);
        if (!Number.isFinite(rate)) return '';
        const displayType = String(row.displayType ?? '');
        const units = calcHourUnitsNumber(row.startAt, row.endAt, displayType);
        const unitMinutes = displayType === 'academic_hour' ? 45 : 60;
        const breakMinutes = Number(row.breakMinutes);
        const breakUnits =
          Number.isFinite(breakMinutes) && breakMinutes > 0
            ? breakMinutes / unitMinutes
            : 0;
        const adjustedUnits = Math.max(0, units - breakUnits);
        const key = `${row.userId}-${getLocalDateKey(row.startAt)}-${row.branchId}`;
        const owner = bestTravelOwnerByUserDateBranch.get(key);
        const travelToAdd =
          owner && owner.workingHourId === row.workingHourId
            ? owner.travelRate
            : 0;
        return rate * adjustedUnits + (Number(travelToAdd) || 0);
      })(),
      row.lectureName ?? '',
    ]);

    const idxStart = headers.indexOf('התחלה');
    const idxEnd = headers.indexOf('סיום');
    const idxBreak = headers.indexOf('הפסקות');
    const idxHours = headers.indexOf('שעות (אקדמי/מלאות)');
    const idxRate = headers.indexOf('תעריף');
    const idxTravel = headers.indexOf('תעריף נסיעות');
    const idxTotal = headers.indexOf('סה״כ');

    generateAndDownloadExcelFile([headers, ...dataRows], {
      fileName: 'salary-generator-flat',
      sheetName: 'Payroll',
      postProcessWorksheet: (ws) => {
        const ref = (ws as any)['!ref'] as string | undefined;
        if (!ref) return;
        const decoded = XLSX.utils.decode_range(ref);

        // תאריך stays plain text (UTC dd/mm/yyyy). התחלה / סיום: same as before — Excel time cells.
        for (let r = decoded.s.r + 1; r <= decoded.e.r; r++) {
          const startAddr = XLSX.utils.encode_cell({ r, c: idxStart });
          const endAddr = XLSX.utils.encode_cell({ r, c: idxEnd });
          const breakAddr = XLSX.utils.encode_cell({ r, c: idxBreak });
          const hoursAddr = XLSX.utils.encode_cell({ r, c: idxHours });
          const rateAddr = XLSX.utils.encode_cell({ r, c: idxRate });
          const travelAddr = XLSX.utils.encode_cell({ r, c: idxTravel });
          const totalAddr = XLSX.utils.encode_cell({ r, c: idxTotal });

          const startCell = (ws as any)[startAddr];
          if (startCell?.v) {
            const serial = parseTimeToExcelSerial(String(startCell.v));
            if (serial !== null) {
              startCell.t = 'n';
              startCell.v = serial;
              startCell.z = 'hh:mm';
            }
          }

          const endCell = (ws as any)[endAddr];
          if (endCell?.v) {
            const serial = parseTimeToExcelSerial(String(endCell.v));
            if (serial !== null) {
              endCell.t = 'n';
              endCell.v = serial;
              endCell.z = 'hh:mm';
            }
          }

          const breakCell = (ws as any)[breakAddr];
          if (breakCell?.v !== undefined && breakCell?.v !== '') {
            const n = Number(breakCell.v);
            if (Number.isFinite(n)) {
              breakCell.t = 'n';
              breakCell.v = n;
              breakCell.z = '0';
            }
          }

          const hoursCell = (ws as any)[hoursAddr];
          if (hoursCell?.v !== undefined && hoursCell?.v !== '') {
            const n = Number(hoursCell.v);
            if (Number.isFinite(n)) {
              hoursCell.t = 'n';
              hoursCell.v = n;
              hoursCell.z = '#,##0.00';
            }
          }

          const rateCell = (ws as any)[rateAddr];
          if (rateCell?.v !== undefined && rateCell?.v !== '') {
            const n = Number(rateCell.v);
            if (Number.isFinite(n)) {
              rateCell.t = 'n';
              rateCell.v = n;
              rateCell.z = '#,##0.00';
            }
          }

          const travelCell = (ws as any)[travelAddr];
          if (travelCell?.v !== undefined && travelCell?.v !== '') {
            const n = Number(travelCell.v);
            if (Number.isFinite(n)) {
              travelCell.t = 'n';
              travelCell.v = n;
              travelCell.z = '#,##0.00';
            }
          }

          const totalCell = (ws as any)[totalAddr];
          if (totalCell?.v !== undefined && totalCell?.v !== '') {
            const n = Number(totalCell.v);
            if (Number.isFinite(n)) {
              totalCell.t = 'n';
              totalCell.v = n;
              totalCell.z = '₪#,##0.00';
            }
          }
        }
      },
    });
    toast.success('הקובץ ירד בהצלחה');
  } catch (e) {
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים...');
  } finally {
    setLoading(false);
  }
};
